import { fonts } from '@/theme/fonts';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface LoginInfoSectionProps {
  loginId: string;
  password: string;
  passwordConfirm: string;
  passwordError: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPasswordConfirmChange: (value: string) => void;
}

const LoginInfoSection = ({
  loginId,
  password,
  passwordConfirm,
  passwordError,
  onChange,
  onPasswordConfirmChange,
}: LoginInfoSectionProps) => {
  const { t } = useTranslation();
  return (
    <Section>
      <SectionTitle>{t('playerSignUp.loginInformation')}</SectionTitle>

      <GridContainer>
        <FormGroup>
          <Label $required>{t('playerSignUp.email')}</Label>
          <Input
            type="email"
            name="loginId"
            value={loginId}
            onChange={onChange}
            placeholder="example@domain.com"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label $required>{t('playerSignUp.password')}</Label>
          <Input
            type="password"
            name="password"
            value={password}
            onChange={onChange}
            placeholder={t('playerSignUp.passwordPlaceholder')}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label $required>{t('playerSignUp.confirmPassword')}</Label>
          <Input
            type="password"
            value={passwordConfirm}
            $error={!!passwordError}
            onChange={(e) => onPasswordConfirmChange(e.target.value)}
            placeholder={t('playerSignUp.confirmPasswordPlaceholder')}
            required
          />
          {passwordError && <ErrorText>{passwordError}</ErrorText>}
        </FormGroup>
      </GridContainer>
    </Section>
  );
};

export default LoginInfoSection;

const Section = styled.section`
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: ${fonts.weight.semiBold};
  color: #374151;
  margin-bottom: 16px;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

interface LabelProps {
  $required?: boolean;
}

const Label = styled.label<LabelProps>`
  font-size: 13px;
  font-weight: ${fonts.weight.semiBold};
  color: #6b7280;

  &::after {
    content: ${(props) => (props.$required ? "' *'" : "''")};
    color: #ff4d4f;
  }
`;

interface InputProps {
  $error?: boolean;
}

const Input = styled.input<InputProps>`
  height: 40px;
  padding: 0 12px;
  font-size: 14px;
  border: 1px solid ${({ $error }) => ($error ? '#ef4444' : '#d1d5db')};
  border-radius: 6px;
  outline: none;
  background: #ffffff;

  &:focus {
    border-color: #2563eb;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ErrorText = styled.p`
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
  color: #ff4d4f;
`;
